// Season markdowns and final sale for the Fashion layout, from the server's
// cwr_fsh_markdown (getFashionMarkdowns). A markdown takes a percentage off, or
// sets a fixed lower price, for one product in one store or in every store,
// between two dates. Final-sale items can't be returned.
//
// The till keeps the last list it fetched (per store) so markdowns still apply
// offline; rows are re-checked against today's date before use. Nothing goes
// into the product tables, so Dexie and SQLite tills behave the same.
import dayjs from "dayjs";
import { getApplicablePrice } from "../getPriceList";
import { roundTo } from "../tillHelpers";

const CACHE_KEY = "fashionMarkdowns";

export const today = (now = new Date()) => dayjs(now).format("YYYY-MM-DD");

const cleanTerm = (s) => String(s ?? "").replace(/["\\]/g, "").trim();

export function buildMarkdownsQuery(bunitId, onDate) {
  const date = onDate ? `, onDate: "${cleanTerm(onDate)}"` : "";
  return {
    query: `query { getFashionMarkdowns(bunitId: "${cleanTerm(bunitId)}"${date}) {
      markdownId bunitId productId season markdownType markdownValue isFinalSale validFrom validTo reason
    }}`,
  };
}

// Server response → markdown rows, or null when the call failed.
export function readMarkdowns(response) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.getFashionMarkdowns;
  if (!Array.isArray(rows)) return null;
  return rows
    .filter((r) => r?.productId && r?.validFrom)
    .map((r) => ({
      id: r.markdownId,
      bunitId: r.bunitId || null,
      productId: r.productId,
      season: r.season || "",
      type: r.markdownType,
      value: r.markdownValue == null ? null : Number(r.markdownValue),
      finalSale: r.isFinalSale === "Y",
      from: String(r.validFrom).slice(0, 10),
      to: r.validTo ? String(r.validTo).slice(0, 10) : null,
      reason: r.reason || "",
    }));
}

// Live on day (YYYY-MM-DD) in this store.
export const isLive = (md, day, bunitId) => Boolean(md) && md.from <= day && (!md.to || md.to >= day) && (!md.bunitId || !bunitId || md.bunitId === bunitId);

// Price after one markdown. A fixed price above the current price is ignored:
// a markdown never puts a price up. precision is the till currency's decimals.
export function markdownPrice(base, md, precision = 2) {
  const price = Number(base) || 0;
  if (!md) return price;
  if (md.type === "PCT" && md.value > 0 && md.value < 100) return roundTo((price * (100 - md.value)) / 100, precision);
  if (md.type === "PRICE" && md.value > 0 && md.value < price) return md.value;
  return price;
}

// What applies to a product today: { price, was, markdown, finalSale }, or null
// when nothing does. The lowest price wins; any live final-sale row makes it
// final sale.
export function dealFor(markdowns, productId, base, day, bunitId, precision = 2) {
  const live = (markdowns || []).filter((md) => md.productId === productId && isLive(md, day, bunitId));
  if (live.length === 0) return null;
  const was = Number(base) || 0;
  let best = null;
  live.forEach((md) => {
    const price = markdownPrice(was, md, precision);
    if (price < was && (!best || price < best.price)) best = { price, markdown: md };
  });
  const finalSale = live.some((md) => md.finalSale);
  if (!best && !finalSale) return null;
  return { price: best ? best.price : was, was, markdown: best?.markdown || null, finalSale };
}

// The price Core would charge before any markdown: the customer's price list
// when the product has one, else its own price (same call as addDefinedProduct).
export function basePrice(product, customer) {
  if (product?.priceList?.length > 0) {
    const selected = getApplicablePrice({
      priceList: product.priceList,
      selectedUomId: product.uomData?.[0]?.csUomId,
      sPriceListId: customer?.sPriceListId,
      alternateUom: product.alternateUom || [],
      primaryUomId: product.uom || null,
    });
    if (selected?.pricestd != null) return Number(selected.pricestd) || 0;
  }
  return Number(product?.sunitprice) || 0;
}

// The product as handed to Core's pickProduct with the markdown price. The
// price list is dropped so Core neither re-prices it on add nor when the
// customer changes; Core's own promotions still run on top. Batched products
// are left alone (their price comes from the batch).
export function withMarkdown(product, deal) {
  if (!deal || deal.price >= deal.was || product?.batchedProduct === "Y") return product;
  return { ...product, sunitprice: deal.price, originalPrice: deal.price, priceList: [] };
}

// The one synced product a scanned code names, or null (no match, or more than one).
export function productForCode(products, code) {
  const c = String(code || "").trim();
  if (!c) return null;
  const hits = (products || []).filter((p) => p && (p.upc === c || p.value === c));
  return hits.length === 1 ? hits[0] : null;
}

// Labels for the cart and size picker, e.g. "30% off" or "Final sale".
export function dealLabel(deal) {
  if (!deal) return "";
  const parts = [];
  if (deal.markdown?.type === "PCT") parts.push(`${deal.markdown.value}% off`);
  else if (deal.markdown) parts.push("Marked down");
  if (deal.finalSale) parts.push("Final sale");
  return parts.join(" · ");
}

// Product ids that were final sale on a bill's date, for the return screen.
export function finalSaleIds(markdowns, day, bunitId) {
  return new Set((markdowns || []).filter((md) => md.finalSale && isLive(md, day, bunitId)).map((md) => md.productId));
}

export function saveCache(bunitId, rows, storage = globalThis.localStorage) {
  try {
    storage?.setItem(`${CACHE_KEY}:${bunitId}`, JSON.stringify({ at: Date.now(), rows }));
  } catch {
    // cache is a convenience; a failed write just means no offline markdowns
  }
}

export function loadCache(bunitId, storage = globalThis.localStorage) {
  try {
    const parsed = JSON.parse(storage?.getItem(`${CACHE_KEY}:${bunitId}`) || "null");
    return Array.isArray(parsed?.rows) ? parsed.rows : [];
  } catch {
    return [];
  }
}

// When this till last saved the store's markdowns, or null if it never has.
export function loadCacheTime(bunitId, storage = globalThis.localStorage) {
  try {
    const at = Number(JSON.parse(storage?.getItem(`${CACHE_KEY}:${bunitId}`) || "null")?.at);
    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return null;
  }
}
